/**
 * The assembler's headless editing model: the room list (committed + in-flight
 * draft), selection, current level, view mode, viewport, and the two gestures
 * (move a room, rotate a room). Mirrors `editor-model/editorState.ts` +
 * `gestures.ts` for the room editor, one abstraction level up: the atoms here
 * are whole rooms, whose geometry is never edited.
 */
import {
  expandPlanBounds,
  type FloorPlanRoom,
  isRoomPlaced,
  type PlanBounds,
  type Point,
  roomPlacement,
  rotateRoomQuarterTurns,
  WALL_THICKNESS,
} from '@kaiizen/kai-floor-plan';
import { createViewport, type InputKind, snapReach, type Viewport } from '../editor-model';
import {
  assemblyBounds,
  assemblyLevels,
  type AssemblyRoom,
  autoPlaceRooms,
  findAssemblyRoom,
  placeAssemblyRoom,
  resetAssemblyLayout,
  roomsOnLevel,
  updateAssemblyRoom,
} from './assemblyModel';
import { snapRoom } from './snapping';
import { type TrueUpResult, trueUpRooms } from './trueUp';

export type AssemblerSelection =
  | { kind: 'none' }
  | { kind: 'room'; id: string };

export type AssemblerGesture =
  /** Translate a room by its body; grab offset keeps the room from jumping under the pointer. */
  | { kind: 'moveRoom'; id: string; grabX: number; grabY: number }
  /** Rotate a room about its primary-rectangle center via the rotation handle. */
  | { kind: 'rotateRoom'; id: string; cx: number; cy: number }
  /** Move a room from the 3D gadget: in plan and in floor elevation, from where it began. */
  | { kind: 'moveRoom3D'; id: string; start: { x: number; y: number; z: number } };

export type AssemblerMode = '2d' | '3d';

export interface AssemblerMutableState {
  /** The committed rooms (placements applied). */
  rooms: AssemblyRoom[];
  /** In-flight working copy during a drag; committed on pointer-up. */
  draftRooms: AssemblyRoom[] | null;
  selection: AssemblerSelection;
  /** The level being viewed; only its rooms render. */
  level: number;
  mode: AssemblerMode;
  viewport: Viewport;
  drag: AssemblerGesture | null;
  /** The dim chip currently open for inline editing. */
  editingKey: string | null;
  readOnly: boolean;
  /** Whether a moved room snaps to its neighbors' walls and openings (the host's preference). */
  snapping: boolean;
  /** Mouse or finger (host-set, see inputKind): the chips' spacing follows it. */
  inputKind: InputKind;
}

/** The level the assembler opens on: the default level when it has rooms, else the lowest with rooms. */
export function initialAssemblyLevel(rooms: readonly AssemblyRoom[]): number {
  const levels = assemblyLevels(rooms);
  const populated = levels.filter(level => roomsOnLevel(rooms, level).length > 0);
  return populated.includes(1) ? 1 : populated[0] ?? levels[0];
}

/** The initial state — pure; shared by the React hook and headless tests. Unplaced rooms get auto-placed. */
export function createAssemblerState(rooms: AssemblyRoom[], readOnly = false, snapping = true, inputKind: InputKind = 'pointer'): AssemblerMutableState {
  const placed = autoPlaceRooms(rooms);
  return {
    rooms: placed,
    draftRooms: null,
    selection: { kind: 'none' },
    level: initialAssemblyLevel(placed),
    mode: '2d',
    viewport: createViewport(),
    drag: null,
    editingKey: null,
    readOnly,
    snapping,
    inputKind,
  };
}

/** The rooms to render: the in-flight draft during a drag, else the committed rooms. */
export const displayRooms = (st: AssemblerMutableState): AssemblyRoom[] => st.draftRooms ?? st.rooms;

/** The rendered rooms on the current level. */
export const levelRooms = (st: AssemblerMutableState): AssemblyRoom[] => roomsOnLevel(displayRooms(st), st.level);

/** The current level's measurement region (all its rooms), or null when empty. */
export const levelBounds = (st: AssemblerMutableState): PlanBounds | null => assemblyBounds(levelRooms(st));

export const selectedAssemblyRoom = (st: AssemblerMutableState): AssemblyRoom | undefined =>
  st.selection.kind === 'room' ? findAssemblyRoom(displayRooms(st), st.selection.id) : undefined;

export function selectAssemblyRoom(st: AssemblerMutableState, selection: AssemblerSelection): void {
  st.selection = selection;
  st.editingKey = null;
}

/** Switch levels; a selection on another level is dropped. */
export function setAssemblerLevel(st: AssemblerMutableState, level: number): void {
  st.level = level;
  const selected = selectedAssemblyRoom(st);
  if (selected && roomPlacement(selected.room).level !== level) selectAssemblyRoom(st, { kind: 'none' });
}

/** Fit the viewport to a measurement region (same padding/floor as the room editor's fit). */
export function fitAssemblyViewport(region: PlanBounds | null, stageW: number, stageH: number, vp: Viewport): Viewport {
  const cw = stageW || 800, ch = stageH || 600;
  if (!region) return { scale: Math.max(2, vp.scale), offset: { x: cw / 2, y: ch / 2 } };
  // The rooms draw their wall bands one thickness outside the measured region.
  const bounds = expandPlanBounds(region, WALL_THICKNESS);
  const bw = Math.max(1, bounds.maxX - bounds.minX), bh = Math.max(1, bounds.maxY - bounds.minY);
  const pad = 0.78;
  const scale = Math.max(2, Math.min((cw * pad) / bw, (ch * pad) / bh));
  const cx = (bounds.minX + bounds.maxX) / 2, cy = (bounds.minY + bounds.maxY) / 2;
  return { scale, offset: { x: cw / 2 - cx * scale, y: ch / 2 - cy * scale } };
}

const beginDraft = (st: AssemblerMutableState): void => {
  st.draftRooms = st.rooms;
};

/** The room's rotation pivot in the whole-property plan. */
const roomCenter = (room: FloorPlanRoom): Point => {
  const { x, y } = roomPlacement(room);
  return { x: x + room.width / 2, y: y + room.length / 2 };
};

/** Start translating a room by its body. */
export function beginRoomMove(st: AssemblerMutableState, id: string, model: Point): void {
  selectAssemblyRoom(st, { kind: 'room', id });
  const target = findAssemblyRoom(st.rooms, id);
  if (!target || st.readOnly) return;
  const { x, y } = roomPlacement(target.room);
  beginDraft(st);
  st.drag = { kind: 'moveRoom', id, grabX: model.x - x, grabY: model.y - y };
}

/** Start rotating a room about its center via the rotation handle. */
export function beginRoomRotate(st: AssemblerMutableState, id: string): void {
  selectAssemblyRoom(st, { kind: 'room', id });
  const target = findAssemblyRoom(st.rooms, id);
  if (!target || st.readOnly) return;
  const center = roomCenter(target.room);
  beginDraft(st);
  st.drag = { kind: 'rotateRoom', id, cx: center.x, cy: center.y };
}

/** Start moving a room from the 3D gadget; `moveRoom3D` advances it by how far the drag has come. */
export function beginRoomMove3D(st: AssemblerMutableState, id: string): void {
  selectAssemblyRoom(st, { kind: 'room', id });
  const target = findAssemblyRoom(st.rooms, id);
  if (!target || st.readOnly) return;
  const { x, y, z } = roomPlacement(target.room);
  beginDraft(st);
  st.drag = { kind: 'moveRoom3D', id, start: { x, y, z } };
}

/**
 * Advance a gadget drag: the room goes to where it began plus `delta` (plan
 * feet x / y, floor elevation z), snapped in plan like a plan move. `reach` is
 * the snap reach at the gadget's depth on screen.
 */
export function moveRoom3D(st: AssemblerMutableState, delta: { x: number; y: number; z: number }, reach: number): void {
  const drag = st.drag;
  if (!drag || drag.kind !== 'moveRoom3D' || !st.draftRooms) return;
  const { x, y } = snappedPlace(st, drag.id, drag.start.x + delta.x, drag.start.y + delta.y, reach);
  st.draftRooms = placeAssemblyRoom(st.rooms, drag.id, { x, y, z: drag.start.z + delta.z });
}

/** A room's plan position snapped to the other rooms on its level (walls to walls, then opening edges along a snapped wall) when snapping is on. */
function snappedPlace(st: AssemblerMutableState, id: string, x: number, y: number, reach: number): Point {
  const target = findAssemblyRoom(st.rooms, id);
  if (!st.snapping || !target) return { x, y };
  const others = roomsOnLevel(st.rooms, st.level).filter(room => room.id !== id).map(room => room.room);
  const delta = snapRoom({ ...target.room, x, y }, others, reach);
  return { x: x + delta.x, y: y + delta.y };
}

/** Advance the active gesture; `model` is the pointer in whole-property plan feet. */
export function moveAssemblerDrag(st: AssemblerMutableState, model: Point): void {
  const drag = st.drag;
  if (!drag || !st.draftRooms) return;
  // Every branch REPLACES the draft with a pure edit of the drag base.
  if (drag.kind === 'moveRoom') {
    st.draftRooms = placeAssemblyRoom(st.rooms, drag.id, snappedPlace(st, drag.id, model.x - drag.grabX, model.y - drag.grabY, snapReach(st.viewport.scale)));
  } else if (drag.kind === 'moveRoom3D') {
    // A gadget drag is advanced by the 3D view through moveRoom3D, never by the plan's pointer.
  } else {
    // The handle rides the room's local 0° direction, so the pointer's bearing
    // from the center IS the rotation, snapped to 5° steps (like free elements).
    const degrees = (Math.atan2(model.y - drag.cy, model.x - drag.cx) * 180) / Math.PI;
    st.draftRooms = placeAssemblyRoom(st.rooms, drag.id, { rotation: Math.round(degrees / 5) * 5 });
  }
}

/** Whether the room list can take a committed edit now: not read-only and no gesture in flight. */
const canEditRooms = (st: AssemblerMutableState): boolean => !st.readOnly && !st.drag && !st.draftRooms;

/**
 * True up the placed rooms on the current level (see `trueUpRooms`) as one
 * committed edit, so a single undo reverses it. Null when nothing can be
 * edited right now.
 */
export function trueUpAssemblerLevel(st: AssemblerMutableState): TrueUpResult | null {
  if (!canEditRooms(st)) return null;
  const placed = levelRooms(st).filter(room => isRoomPlaced(room.room));
  const result = trueUpRooms(placed);
  const adjusted = new Map(result.rooms.map(room => [room.id, room]));
  if (result.rooms.some((room, index) => room !== placed[index])) st.rooms = st.rooms.map(room => adjusted.get(room.id) ?? room);
  return result;
}

/**
 * Turn the selected room by quarter turns (positive is clockwise) in its own
 * data, as one committed edit: the edges roll and width and length swap, the
 * room stays centered where it was, and its placement angle is kept. No-op
 * with nothing selected, mid-drag, or read-only.
 */
export function rotateSelectedAssemblyRoom(st: AssemblerMutableState, quarterTurns: number): void {
  const selected = selectedAssemblyRoom(st);
  if (!selected || !canEditRooms(st)) return;
  st.rooms = updateAssemblyRoom(st.rooms, selected.id, room => rotateRoomQuarterTurns(room, quarterTurns));
}

/**
 * Put every room back to the automatic initial placement (see
 * `resetAssemblyLayout`) as one committed edit, so a single undo reverses it.
 * No-op when read-only or mid-gesture.
 */
export function resetAssemblerLayout(st: AssemblerMutableState): void {
  if (canEditRooms(st)) st.rooms = resetAssemblyLayout(st.rooms);
}

/** Commit the active gesture: the draft becomes the rooms. */
export function endAssemblerDrag(st: AssemblerMutableState): void {
  if (!st.drag) return;
  const committed = st.draftRooms ?? st.rooms;
  st.drag = null;
  st.draftRooms = null;
  st.rooms = committed;
}
